// src/pages/admin/Categories.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiX,
  FiCheck,
  FiAlertCircle,
  FiImage,
  FiToggleLeft,
  FiToggleRight,
  FiLoader,
  FiTag,
  FiArrowUp,
  FiArrowDown,
  FiEye,
  FiEyeOff,
} from "react-icons/fi";
import { MdCategory } from "react-icons/md";

// ── Styles ─────────────────────────────────────────────────────────────
const C = {
  bg: "#050507",
  surface: "#fff",
  card: "#13131a",
  border: "rgba(255,255,255,0.06)",
  primary: "#8b5cf6",
  primaryGradient: "linear-gradient(135deg,#6366f1 0%,#a855f7 100%)",
  green: "#10b981",
  red: "#f43f5e",
  orange: "#f59e0b",
  blue: "#3b82f6",
  text: "#333",
  muted: "#8b949e",
  white: "#ffffff",
};

const S = {
  page: {
    padding: "0",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 24,
    flexWrap: "wrap",
  },
  searchWrap: {
    position: "relative",
    flex: 1,
    minWidth: 200,
    maxWidth: 340,
  },
  searchIcon: {
    position: "absolute",
    left: 12,
    top: "50%",
    transform: "translateY(-50%)",
    color: C.muted,
    pointerEvents: "none",
    fontSize: 15,
  },
  searchInput: {
    width: "100%",
    padding: "9px 12px 9px 36px",
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    color: C.text,
    fontSize: 13,
    outline: "none",
    boxSizing: "border-box",
  },
  addBtn: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    padding: "9px 18px",
    background: C.primaryGradient,
    border: "none",
    borderRadius: 8,
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: 16,
  },
  card: {
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    overflow: "hidden",
    transition: "border-color 0.2s, transform 0.15s",
  },
  cardImg: {
    width: "100%",
    height: 140,
    objectFit: "cover",
    background: C.surface,
    display: "block",
  },
  cardImgPlaceholder: {
    width: "100%",
    height: 140,
    background: C.surface,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: C.muted,
    fontSize: 32,
  },
  cardBody: {
    padding: "14px 16px",
  },
  cardRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  cardName: {
    fontSize: 15,
    fontWeight: 700,
    color: C.text,
    margin: 0,
  },
  cardSlug: {
    fontSize: 11,
    color: C.muted,
    fontFamily: "monospace",
    background: "rgba(255,255,255,0.04)",
    padding: "2px 6px",
    borderRadius: 4,
    margin: 0,
  },
  cardDesc: {
    fontSize: 12,
    color: C.muted,
    margin: "6px 0 10px",
    lineHeight: 1.5,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  cardFooter: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    borderTop: `1px solid ${C.border}`,
    paddingTop: 10,
    marginTop: 10,
  },
  badge: (active) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 8px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 600,
    background: active ? "rgba(16,185,129,0.15)" : "rgba(244,63,94,0.12)",
    color: active ? C.green : C.red,
    border: `1px solid ${active ? "rgba(16,185,129,0.3)" : "rgba(244,63,94,0.3)"}`,
  }),
  orderBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 8px",
    borderRadius: 20,
    fontSize: 11,
    color: C.muted,
    background: "rgba(255,255,255,0.05)",
    marginLeft: "auto",
  },
  megaBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 8px",
    borderRadius: 20,
    fontSize: 10,
    fontWeight: 600,
    background: "rgba(139,92,246,0.15)",
    color: C.primary,
    border: `1px solid rgba(139,92,246,0.3)`,
  },
  iconBtn: (color) => ({
    width: 30,
    height: 30,
    borderRadius: 7,
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: 14,
    background: color + "18",
    color,
    transition: "background 0.15s",
  }),
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.65)",
    backdropFilter: "blur(4px)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },
  modal: {
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 16,
    width: "100%",
    maxWidth: 800,
    maxHeight: "90vh",
    overflowY: "auto",
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px 24px 16px",
    borderBottom: `1px solid ${C.border}`,
  },
  modalTitle: {
    fontSize: 17,
    fontWeight: 700,
    color: C.text,
    margin: 0,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  modalBody: {
    padding: "20px 24px 24px",
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 14,
    marginBottom: 14,
  },
  formGroup: {
    marginBottom: 14,
  },
  label: {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: C.muted,
    marginBottom: 6,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  input: {
    width: "100%",
    padding: "9px 12px",
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    color: C.text,
    fontSize: 13,
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.15s",
  },
  textarea: {
    width: "100%",
    padding: "9px 12px",
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    color: C.text,
    fontSize: 13,
    outline: "none",
    resize: "vertical",
    minHeight: 72,
    boxSizing: "border-box",
    fontFamily: "inherit",
  },
  toggleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 0",
    borderBottom: `1px solid ${C.border}`,
    marginBottom: 14,
  },
  toggleLabel: {
    fontSize: 13,
    color: C.text,
    fontWeight: 500,
  },
  toggleBtn: (active) => ({
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: 26,
    color: active ? C.green : C.muted,
    display: "flex",
    alignItems: "center",
    padding: 0,
  }),
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    paddingTop: 16,
    borderTop: `1px solid ${C.border}`,
    marginTop: 4,
  },
  cancelBtn: {
    padding: "9px 18px",
    background: "rgba(255,255,255,0.06)",
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    color: C.text,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  saveBtn: {
    padding: "9px 22px",
    background: C.primaryGradient,
    border: "none",
    borderRadius: 8,
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  deleteBtn: {
    padding: "9px 18px",
    background: "rgba(244,63,94,0.15)",
    border: `1px solid rgba(244,63,94,0.3)`,
    borderRadius: 8,
    color: C.red,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  toastWrap: {
    position: "fixed",
    top: 20,
    right: 20,
    zIndex: 9999,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  toast: (type) => ({
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 18px",
    borderRadius: 10,
    background:
      type === "error" ? "rgba(244,63,94,0.15)" : "rgba(16,185,129,0.15)",
    border: `1px solid ${type === "error" ? "rgba(244,63,94,0.4)" : "rgba(16,185,129,0.4)"}`,
    color: type === "error" ? C.red : C.green,
    fontSize: 13,
    fontWeight: 600,
    backdropFilter: "blur(8px)",
    minWidth: 240,
  }),
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
    color: C.muted,
  },
  spinnerWrap: {
    display: "flex",
    justifyContent: "center",
    padding: "60px 0",
    color: C.primary,
    fontSize: 28,
  },
  imgPreview: {
    width: "100%",
    height: 100,
    objectFit: "cover",
    borderRadius: 8,
    marginTop: 8,
    border: `1px solid ${C.border}`,
  },
};

// ── API helpers ───────────────────────────────────────────────────────────
const BASE = "https://lightslategrey-mole-360881.hostingersite.com/api";

async function catFetch(endpoint, options = {}) {
  const token = localStorage.getItem("kkb_token");
  const cfg = {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    ...options,
  };

  if (
    cfg.body &&
    typeof cfg.body === "object" &&
    !(cfg.body instanceof FormData)
  ) {
    cfg.body = JSON.stringify(cfg.body);
  }

  const res = await fetch(`${BASE}/admin/categories${endpoint}`, cfg);

  let data = {};
  const contentType = res.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    data = await res.json();
  }

  if (!res.ok) {
    throw new Error(
      data.message || data.error || `Request failed with status ${res.status}`,
    );
  }

  return data;
}

const catGet = () => catFetch("");
const catPost = (body) => catFetch("", { method: "POST", body });
const catPut = (id, body) => catFetch(`/${id}`, { method: "PUT", body });
const catDelete = (id) => catFetch(`/${id}`, { method: "DELETE" });

// ── Mega Menu Editor Component ──────────────────────────────────────────────
function MegaMenuEditor({ value, onChange }) {
  const [columns, setColumns] = useState(value || []);

  const addColumn = () => {
    const updated = [...columns, { title: "", links: [] }];
    setColumns(updated);
    onChange(updated);
  };

  const updateColumn = (idx, field, val) => {
    const updated = [...columns];
    updated[idx][field] = val;
    setColumns(updated);
    onChange(updated);
  };

  const addLink = (colIdx) => {
    const updated = [...columns];
    updated[colIdx].links.push({ label: "", slug: "", image: "" });
    setColumns(updated);
    onChange(updated);
  };

  const updateLink = (colIdx, linkIdx, field, val) => {
    const updated = [...columns];
    updated[colIdx].links[linkIdx][field] = val;
    setColumns(updated);
    onChange(updated);
  };

  const removeLink = (colIdx, linkIdx) => {
    const updated = [...columns];
    updated[colIdx].links.splice(linkIdx, 1);
    setColumns(updated);
    onChange(updated);
  };

  const removeColumn = (colIdx) => {
    const updated = columns.filter((_, i) => i !== colIdx);
    setColumns(updated);
    onChange(updated);
  };

  return (
    <div style={{ marginBottom: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <label style={S.label}>Mega Menu Columns</label>
        <button
          type="button"
          onClick={addColumn}
          style={{
            ...S.iconBtn(C.primary),
            width: "auto",
            padding: "4px 12px",
            gap: 4,
          }}
        >
          <FiPlus size={12} /> Add Column
        </button>
      </div>
      {columns.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: 20,
            color: C.muted,
            fontSize: 12,
          }}
        >
          No columns added. Click "Add Column" to create mega menu structure.
        </div>
      )}
      {columns.map((col, colIdx) => (
        <div
          key={colIdx}
          style={{
            background: "rgba(255,255,255,0.03)",
            padding: 12,
            borderRadius: 8,
            marginBottom: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 10,
            }}
          >
            <input
              style={{ ...S.input, width: "calc(100% - 40px)" }}
              placeholder="Column Title (e.g., LUNGIES)"
              value={col.title}
              onChange={(e) => updateColumn(colIdx, "title", e.target.value)}
            />
            <button
              type="button"
              onClick={() => removeColumn(colIdx)}
              style={S.iconBtn(C.red)}
            >
              <FiTrash2 size={12} />
            </button>
          </div>
          <div style={{ marginLeft: 12 }}>
            {col.links.map((link, linkIdx) => (
              <div
                key={linkIdx}
                style={{
                  marginBottom: 12,
                  borderBottom: `1px solid rgba(255,255,255,0.05)`,
                  paddingBottom: 12,
                }}
              >
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <input
                    style={{ ...S.input, flex: 1 }}
                    placeholder="Label"
                    value={link.label}
                    onChange={(e) =>
                      updateLink(colIdx, linkIdx, "label", e.target.value)
                    }
                  />
                  <input
                    style={{ ...S.input, flex: 1 }}
                    placeholder="Slug"
                    value={link.slug}
                    onChange={(e) =>
                      updateLink(colIdx, linkIdx, "slug", e.target.value)
                    }
                  />
                  <button
                    type="button"
                    onClick={() => removeLink(colIdx, linkIdx)}
                    style={S.iconBtn(C.red)}
                  >
                    <FiX size={12} />
                  </button>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    style={{ ...S.input, flex: 2 }}
                    placeholder="Image URL (for hover preview)"
                    value={link.image || ""}
                    onChange={(e) =>
                      updateLink(colIdx, linkIdx, "image", e.target.value)
                    }
                  />
                  {link.image && (
                    <div
                      style={{ position: "relative", width: 50, height: 50 }}
                    >
                      <img
                        src={link.image}
                        alt={link.label}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: 6,
                        }}
                        onError={(e) => (e.target.style.display = "none")}
                      />
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>
                  Recommended: 800x600px image for best display
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => addLink(colIdx)}
              style={{
                ...S.iconBtn(C.primary),
                width: "auto",
                padding: "4px 12px",
                marginTop: 4,
              }}
            >
              <FiPlus size={12} /> Add Link
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Simple Sub Links Editor Component ────────────────────────────────────────
function SimpleSubLinksEditor({ value, onChange }) {
  const [links, setLinks] = useState(value || []);

  const addLink = () => {
    setLinks([...links, { label: "", slug: "", image: "" }]);
    onChange([...links, { label: "", slug: "", image: "" }]);
  };

  const updateLink = (idx, field, val) => {
    const updated = [...links];
    updated[idx][field] = val;
    setLinks(updated);
    onChange(updated);
  };

  const removeLink = (idx) => {
    const updated = links.filter((_, i) => i !== idx);
    setLinks(updated);
    onChange(updated);
  };

  return (
    <div style={{ marginBottom: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <label style={S.label}>Simple Sub-menu Links</label>
        <button
          type="button"
          onClick={addLink}
          style={{
            ...S.iconBtn(C.primary),
            width: "auto",
            padding: "4px 12px",
            gap: 4,
          }}
        >
          <FiPlus size={12} /> Add Link
        </button>
      </div>
      {links.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: 20,
            color: C.muted,
            fontSize: 12,
          }}
        >
          No sub-links added. Click "Add Link" to create dropdown items.
        </div>
      )}
      {links.map((link, idx) => (
        <div
          key={idx}
          style={{
            marginBottom: 12,
            borderBottom: `1px solid ${C.border}`,
            paddingBottom: 12,
          }}
        >
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <input
              style={{ ...S.input, flex: 1 }}
              placeholder="Label (e.g., Kaavi Collection)"
              value={link.label}
              onChange={(e) => updateLink(idx, "label", e.target.value)}
            />
            <input
              style={{ ...S.input, flex: 1 }}
              placeholder="Slug (e.g., kaavi-collection)"
              value={link.slug}
              onChange={(e) => updateLink(idx, "slug", e.target.value)}
            />
            <button
              type="button"
              onClick={() => removeLink(idx)}
              style={S.iconBtn(C.red)}
            >
              <FiX size={12} />
            </button>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              style={{ ...S.input, flex: 2 }}
              placeholder="Image URL (for hover preview)"
              value={link.image || ""}
              onChange={(e) => updateLink(idx, "image", e.target.value)}
            />
            {link.image && (
              <div style={{ position: "relative", width: 50, height: 50 }}>
                <img
                  src={link.image}
                  alt={link.label}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: 6,
                  }}
                  onError={(e) => (e.target.style.display = "none")}
                />
              </div>
            )}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
            Recommended: 800x600px image for best display
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Inline Toast ──────────────────────────────────────────────────────────────
function LocalToast({ toasts }) {
  return (
    <div style={S.toastWrap}>
      {toasts.map((t) => (
        <div key={t.id} style={S.toast(t.type)}>
          {t.type === "error" ? <FiAlertCircle /> : <FiCheck />}
          <span>{t.msg}</span>
        </div>
      ))}
    </div>
  );
}

// ── Category Form Modal ───────────────────────────────────────────────────────
const EMPTY_FORM = {
  name: "",
  slug: "",
  description: "",
  image: "",
  bannerImage: "",
  displayOrder: 0,
  metaTitle: "",
  metaDescription: "",
  isActive: true,
  isMegaMenu: false,
  megaMenuColumns: [],
  simpleSubLinks: [],
};

function slugify(str) {
  return str
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

function CategoryModal({ category, onClose, onSaved, toast }) {
  const isEdit = !!category?._id;
  const [form, setForm] = useState(() => {
    if (isEdit) {
      console.log("Editing category:", category); // Debug log
      return JSON.parse(JSON.stringify(category));
    }
    return { ...EMPTY_FORM };
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  function setField(key, val) {
    setForm((f) => {
      const updated = { ...f, [key]: val };
      if (key === "name" && !isEdit) updated.slug = slugify(val);
      return updated;
    });
  }

  async function handleSave() {
    if (!form.name.trim()) {
      toast("Name is required", "error");
      return;
    }

    setSaving(true);
    try {
      console.log("Saving category:", form); // Debug log
      if (isEdit) {
        await catPut(category._id, form);
        toast("Category updated successfully");
      } else {
        await catPost(form);
        toast("Category created successfully");
      }
      onSaved();
      onClose();
    } catch (e) {
      console.error("Save error:", e);
      toast(e.message || "Failed to save category", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await catDelete(category._id);
      toast("Category deleted successfully");
      onSaved();
      onClose();
    } catch (e) {
      console.error("Delete error:", e);
      toast(e.message || "Failed to delete category", "error");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div
      style={S.overlay}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={S.modal}>
        <div style={S.modalHeader}>
          <h3 style={S.modalTitle}>
            <MdCategory style={{ color: "#8b5cf6" }} />
            {isEdit ? "Edit Category" : "New Category"}
          </h3>
          <button
            onClick={onClose}
            style={{ ...S.iconBtn(C.muted), background: "none" }}
          >
            <FiX />
          </button>
        </div>

        <div style={S.modalBody}>
          {/* Name + Slug */}
          <div style={S.formRow}>
            <div style={S.formGroup}>
              <label style={S.label}>Name *</label>
              <input
                style={S.input}
                value={form.name || ""}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="e.g. Lungies"
              />
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Slug</label>
              <input
                style={S.input}
                value={form.slug || ""}
                onChange={(e) => setField("slug", slugify(e.target.value))}
                placeholder="auto-generated"
              />
            </div>
          </div>

          {/* Description */}
          <div style={S.formGroup}>
            <label style={S.label}>Description</label>
            <textarea
              style={S.textarea}
              value={form.description || ""}
              onChange={(e) => setField("description", e.target.value)}
              placeholder="Short description of this category…"
            />
          </div>

          {/* Image URLs */}
          <div style={S.formRow}>
            <div style={S.formGroup}>
              <label style={S.label}>Card Image URL</label>
              <input
                style={S.input}
                value={form.image || ""}
                onChange={(e) => setField("image", e.target.value)}
                placeholder="https://…"
              />
              {form.image && (
                <img
                  src={form.image}
                  alt="preview"
                  style={S.imgPreview}
                  onError={(e) => (e.target.style.display = "none")}
                />
              )}
            </div>
            <div style={S.formGroup}>
              <label style={S.label}>Banner Image URL</label>
              <input
                style={S.input}
                value={form.bannerImage || ""}
                onChange={(e) => setField("bannerImage", e.target.value)}
                placeholder="https://…"
              />
              {form.bannerImage && (
                <img
                  src={form.bannerImage}
                  alt="preview"
                  style={S.imgPreview}
                  onError={(e) => (e.target.style.display = "none")}
                />
              )}
            </div>
          </div>

          {/* Display Order + Active */}
          <div style={S.formRow}>
            <div style={S.formGroup}>
              <label style={S.label}>Display Order</label>
              <input
                style={S.input}
                type="number"
                value={form.displayOrder || 0}
                onChange={(e) =>
                  setField("displayOrder", parseInt(e.target.value) || 0)
                }
              />
            </div>
            <div
              style={{
                ...S.formGroup,
                display: "flex",
                alignItems: "flex-end",
                paddingBottom: 2,
              }}
            >
              <div
                style={{
                  ...S.toggleRow,
                  flex: 1,
                  margin: 0,
                  border: "none",
                  paddingBottom: 0,
                }}
              >
                <span style={S.toggleLabel}>Active</span>
                <button
                  onClick={() => setField("isActive", !form.isActive)}
                  style={S.toggleBtn(form.isActive)}
                  type="button"
                >
                  {form.isActive ? <FiToggleRight /> : <FiToggleLeft />}
                </button>
              </div>
            </div>
          </div>

          {/* Mega Menu Toggle */}
          <div style={S.toggleRow}>
            <span style={S.toggleLabel}>Enable Mega Menu</span>
            <button
              onClick={() => setField("isMegaMenu", !form.isMegaMenu)}
              style={S.toggleBtn(form.isMegaMenu)}
              type="button"
            >
              {form.isMegaMenu ? <FiToggleRight /> : <FiToggleLeft />}
            </button>
          </div>

          {/* Mega Menu Editor (if enabled) */}
          {form.isMegaMenu && (
            <MegaMenuEditor
              value={form.megaMenuColumns || []}
              onChange={(val) => setField("megaMenuColumns", val)}
            />
          )}

          {/* Simple Sub-links Editor (if not mega menu) */}
          {!form.isMegaMenu && (
            <SimpleSubLinksEditor
              value={form.simpleSubLinks || []}
              onChange={(val) => setField("simpleSubLinks", val)}
            />
          )}

          {/* SEO */}
          <div style={{ ...S.formGroup, marginBottom: 0 }}>
            <label style={S.label}>Meta Title</label>
            <input
              style={{ ...S.input, marginBottom: 10 }}
              value={form.metaTitle || ""}
              onChange={(e) => setField("metaTitle", e.target.value)}
              placeholder="SEO title…"
            />
            <label style={S.label}>Meta Description</label>
            <textarea
              style={{ ...S.textarea, minHeight: 54 }}
              value={form.metaDescription || ""}
              onChange={(e) => setField("metaDescription", e.target.value)}
              placeholder="SEO description…"
            />
          </div>

          <div style={S.modalFooter}>
            {isEdit && !confirmDelete && (
              <button
                style={S.deleteBtn}
                onClick={() => setConfirmDelete(true)}
              >
                <FiTrash2 /> Delete
              </button>
            )}
            {confirmDelete && (
              <button
                style={S.deleteBtn}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? (
                  <FiLoader style={{ animation: "spin 1s linear infinite" }} />
                ) : (
                  <FiTrash2 />
                )}
                Confirm Delete
              </button>
            )}
            <button
              style={S.cancelBtn}
              onClick={onClose}
              disabled={saving || deleting}
            >
              Cancel
            </button>
            <button style={S.saveBtn} onClick={handleSave} disabled={saving}>
              {saving ? (
                <FiLoader style={{ animation: "spin 1s linear infinite" }} />
              ) : (
                <FiCheck />
              )}
              {isEdit ? "Update" : "Create"}
            </button>
          </div>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Category Card ─────────────────────────────────────────────────────────────
function CategoryCard({ cat, onEdit }) {
  return (
    <div
      style={S.card}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "rgba(139,92,246,0.4)";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = C.border;
        e.currentTarget.style.transform = "none";
      }}
    >
      {cat.image ? (
        <img
          src={cat.image}
          alt={cat.name}
          style={S.cardImg}
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      ) : (
        <div style={S.cardImgPlaceholder}>
          <FiImage />
        </div>
      )}
      <div style={S.cardBody}>
        <div style={S.cardRow}>
          <p style={S.cardName}>{cat.name}</p>
          <button style={S.iconBtn(C.primary)} onClick={() => onEdit(cat)}>
            <FiEdit2 />
          </button>
        </div>
        <p style={S.cardSlug}>/{cat.slug}</p>
        {cat.description && <p style={S.cardDesc}>{cat.description}</p>}
        <div style={S.cardFooter}>
          <span style={S.badge(cat.isActive)}>
            {cat.isActive ? <FiEye size={10} /> : <FiEyeOff size={10} />}
            {cat.isActive ? "Active" : "Inactive"}
          </span>
          {cat.isMegaMenu && <span style={S.megaBadge}>Mega Menu</span>}
          <span style={S.orderBadge}>
            <FiTag size={10} /> Order: {cat.displayOrder}
          </span>
        </div>
        {!cat.isMegaMenu &&
          cat.simpleSubLinks &&
          cat.simpleSubLinks.length > 0 && (
            <div style={{ fontSize: 10, color: C.muted, marginTop: 8 }}>
              {cat.simpleSubLinks.length} sub-link(s)
            </div>
          )}
        {cat.isMegaMenu &&
          cat.megaMenuColumns &&
          cat.megaMenuColumns.length > 0 && (
            <div style={{ fontSize: 10, color: C.muted, marginTop: 8 }}>
              {cat.megaMenuColumns.length} column(s)
            </div>
          )}
      </div>
    </div>
  );
}

// ── Main Categories Component ─────────────────────────────────────────────────
export default function Categories({ toast: parentToast }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modalCat, setModalCat] = useState(null);
  const [toasts, setToasts] = useState([]);

  const toast = useCallback(
    (msg, type = "success") => {
      if (parentToast) {
        parentToast(msg, type);
      } else {
        const id = Date.now();
        setToasts((p) => [...p, { id, msg, type }]);
        setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 4000);
      }
    },
    [parentToast],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await catGet();
      setCategories(data.categories || []);
    } catch (e) {
      console.error("Error loading categories:", e);
      toast(e.message, "error");
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = categories.filter(
    (c) =>
      c.name?.toLowerCase().includes(search.toLowerCase()) ||
      c.slug?.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div style={S.page}>
      {!parentToast && <LocalToast toasts={toasts} />}

      <div style={S.toolbar}>
        <div style={S.searchWrap}>
          <FiSearch style={S.searchIcon} />
          <input
            style={S.searchInput}
            placeholder="Search categories…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button style={S.addBtn} onClick={() => setModalCat({})}>
          <FiPlus /> New Category
        </button>
      </div>

      <p style={{ color: C.muted, fontSize: 13, marginBottom: 16 }}>
        {filtered.length} {filtered.length === 1 ? "category" : "categories"}
      </p>

      {loading ? (
        <div style={S.spinnerWrap}>
          <FiLoader style={{ animation: "spin 1s linear infinite" }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : filtered.length === 0 ? (
        <div style={S.emptyState}>
          <MdCategory size={40} style={{ marginBottom: 12 }} />
          <p style={{ fontSize: 15, margin: 0 }}>No categories found</p>
          <p style={{ fontSize: 13, marginTop: 6 }}>
            {search
              ? "Try a different search term"
              : "Click 'New Category' to add one"}
          </p>
        </div>
      ) : (
        <div style={S.grid}>
          {filtered.map((cat) => (
            <CategoryCard key={cat._id} cat={cat} onEdit={setModalCat} />
          ))}
        </div>
      )}

      {modalCat !== null && (
        <CategoryModal
          category={modalCat._id ? modalCat : null}
          onClose={() => setModalCat(null)}
          onSaved={load}
          toast={toast}
        />
      )}
    </div>
  );
}
